import { supabase } from '@/integrations/supabase/client';

type CacheEntry = { data: any; timestamp: number };

// Global geocoding cache - shared across all components
const geocodingCache = new Map<string, CacheEntry>();
const pendingRequests = new Map<string, Promise<any>>();

// Geocoding results are effectively static; favor UX speed over aggressive expiry.
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_MAX_SIZE = 1000;

const STORAGE_KEY = 'lr_geocoding_cache_v1';
const STORAGE_MAX_ENTRIES = 300;

// Token caching - tokens rarely change, cache for 1 hour
const TOKEN_CACHE_KEY = 'lr_mapbox_token_v2';
const TOKEN_CACHE_TTL = 60 * 60 * 1000; // 1 hour

let cachedToken: string | null = null;
let tokenCacheTimestamp = 0;

// Rate limiting - max 30 requests per minute as safety net
const RATE_LIMIT_WINDOW = 60 * 1000; // 1 minute
const RATE_LIMIT_MAX = 30;
let rateLimitRequests: number[] = [];

const getCacheKey = (query: string) => query.toLowerCase().trim();

const isFresh = (entry?: CacheEntry) => !!entry && Date.now() - entry.timestamp < CACHE_TTL;

const normalizeFeatures = (data: any) => (Array.isArray(data?.features) ? data.features : []);

const filterFeaturesByQuery = (features: any[], rawQuery: string) => {
  const q = rawQuery.toLowerCase().trim();
  if (!q) return features.slice(0, 10);

  return features
    .filter((f) => {
      const hay = `${f?.text ?? ''} ${f?.place_name ?? ''} ${f?.place_name_en ?? ''}`.toLowerCase();
      return hay.includes(q);
    })
    .slice(0, 10);
};

/**
 * Check rate limit - returns true if request is allowed
 */
const checkRateLimit = (): boolean => {
  const now = Date.now();
  // Remove requests outside the window
  rateLimitRequests = rateLimitRequests.filter(t => now - t < RATE_LIMIT_WINDOW);
  
  if (rateLimitRequests.length >= RATE_LIMIT_MAX) {
    console.warn('[Mapbox] Rate limit exceeded - request blocked');
    return false;
  }
  
  rateLimitRequests.push(now);
  
  if (rateLimitRequests.length > RATE_LIMIT_MAX * 0.8) {
    console.warn(`[Mapbox] Approaching rate limit: ${rateLimitRequests.length}/${RATE_LIMIT_MAX} requests in last minute`);
  }
  
  return true;
};

const loadCacheFromStorage = () => {
  if (typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw) as Record<string, CacheEntry>;
    Object.entries(parsed).forEach(([k, v]) => {
      if (v && typeof v.timestamp === 'number') {
        geocodingCache.set(k, v);
      }
    });
    
    // Also load cached token
    const tokenData = window.localStorage.getItem(TOKEN_CACHE_KEY);
    if (tokenData) {
      const { token, timestamp } = JSON.parse(tokenData);
      if (token && timestamp && Date.now() - timestamp < TOKEN_CACHE_TTL) {
        cachedToken = token;
        tokenCacheTimestamp = timestamp;
      }
    }
  } catch {
    // ignore
  }
};

const persistCacheToStorage = (() => {
  let timer: number | undefined;

  return () => {
    if (typeof window === 'undefined') return;
    if (timer) window.clearTimeout(timer);

    timer = window.setTimeout(() => {
      try {
        const entries = Array.from(geocodingCache.entries())
          .filter(([, v]) => isFresh(v))
          .slice(-STORAGE_MAX_ENTRIES);

        const obj: Record<string, CacheEntry> = {};
        entries.forEach(([k, v]) => {
          obj[k] = v;
        });

        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
      } catch {
        // ignore
      }
    }, 250);
  };
})();

const persistTokenToStorage = (token: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TOKEN_CACHE_KEY, JSON.stringify({
      token,
      timestamp: Date.now()
    }));
  } catch {
    // ignore
  }
};

loadCacheFromStorage();

/**
 * Read exact cache entry without network.
 */
export const getGeocodingSuggestionsCached = (query: string): any | null => {
  const key = getCacheKey(query);
  const cached = geocodingCache.get(key);
  return isFresh(cached) ? cached!.data : null;
};

/**
 * Best-effort instant suggestions from closest cached prefix.
 * Used to render suggestions immediately while the exact query fetch happens.
 */
export const getGeocodingSuggestionsClosestCached = (query: string): any | null => {
  const key = getCacheKey(query);
  if (!key) return null;

  // Prefer longest cached prefix that is a prefix of the current query (e.g. "ne" for "new")
  let bestKey = '';
  let bestEntry: CacheEntry | undefined;

  for (const [k, v] of geocodingCache.entries()) {
    if (!isFresh(v)) continue;
    if (key.startsWith(k) && k.length > bestKey.length) {
      bestKey = k;
      bestEntry = v;
    }
  }

  if (bestEntry) {
    const features = filterFeaturesByQuery(normalizeFeatures(bestEntry.data), key);
    return features.length ? { features } : null;
  }

  // If the user typed a short query (e.g. "n"), aggregate a few cached keys starting with it
  if (key.length === 1) {
    const candidates = Array.from(geocodingCache.entries())
      .filter(([k, v]) => isFresh(v) && k.startsWith(key))
      .sort((a, b) => a[0].length - b[0].length)
      .slice(0, 6);

    const seen = new Set<string>();
    const merged: any[] = [];

    for (const [, v] of candidates) {
      for (const f of normalizeFeatures(v.data)) {
        const id = String(f?.id ?? '');
        if (!id || seen.has(id)) continue;
        seen.add(id);
        merged.push(f);
        if (merged.length >= 10) break;
      }
      if (merged.length >= 10) break;
    }

    const features = filterFeaturesByQuery(merged, key);
    return features.length ? { features } : null;
  }

  return null;
};

/**
 * Get Mapbox token from server for client-side map initialization.
 * Token is cached in memory and localStorage for 1 hour.
 */
export const getMapboxToken = async (): Promise<string | null> => {
  // Check in-memory cache first
  if (cachedToken && Date.now() - tokenCacheTimestamp < TOKEN_CACHE_TTL) {
    return cachedToken;
  }

  // Rate limit check
  if (!checkRateLimit()) {
    // Return cached token even if expired, better than nothing
    return cachedToken;
  }

  try {
    const { data, error } = await supabase.functions.invoke('get-mapbox-token');

    if (error || !data?.token) {
      console.error('Error getting Mapbox token:', error);
      return cachedToken; // Return stale cache if available
    }

    // Update cache
    cachedToken = data.token;
    tokenCacheTimestamp = Date.now();
    persistTokenToStorage(data.token);

    return data.token;
  } catch (error) {
    console.error('Error getting Mapbox token:', error);
    return cachedToken; // Return stale cache if available
  }
};

/**
 * Get geocoding suggestions using Supabase edge function (uses server-side MAPBOX_TOKEN)
 * Results are cached for instant response on repeated queries
 */
export const getGeocodingSuggestions = async (query: string): Promise<any> => {
  const cacheKey = getCacheKey(query);

  // Check cache first
  const cached = geocodingCache.get(cacheKey);
  if (isFresh(cached)) {
    return cached!.data;
  }

  // Rate limit check
  if (!checkRateLimit()) {
    // Return closest cached result if rate limited
    const closest = getGeocodingSuggestionsClosestCached(query);
    if (closest) return closest;
    throw new Error('Rate limit exceeded');
  }

  // Dedupe concurrent requests for the same query
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  const requestPromise = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('geocoding', {
        body: { query },
      });

      if (error) {
        throw error;
      }

      // Cache the result
      if (geocodingCache.size >= CACHE_MAX_SIZE) {
        const firstKey = geocodingCache.keys().next().value;
        if (firstKey) geocodingCache.delete(firstKey);
      }
      geocodingCache.set(cacheKey, { data, timestamp: Date.now() });
      persistCacheToStorage();

      return data;
    } catch (error) {
      console.error('Error getting geocoding suggestions:', error);
      throw error;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  })();

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
};


/**
 * Get truck route between two coordinates using Mapbox Directions API
 */
/**
 * Get truck route between two coordinates using cached Directions Edge Function
 */
export const getTruckRoute = async (
  startCoords: [number, number], 
  endCoords: [number, number]
): Promise<{
  distance: number;
  duration: number;
  geometry: any;
  distanceText: string;
  durationText: string;
} | null> => {
  // Rate limit check
  if (!checkRateLimit()) {
    console.warn('[Mapbox] Rate limited - skipping directions request');
    return null;
  }

  try {
    const { data, error } = await supabase.functions.invoke('directions', {
      body: {
        origin: startCoords,
        destination: endCoords
      }
    });

    if (error) {
      console.error('Directions API error:', error);
      return null;
    }

    if (data?.distance !== undefined) {
      return {
        distance: data.distance,
        duration: data.duration,
        geometry: data.geometry,
        distanceText: data.distanceText,
        durationText: data.durationText
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting truck route:', error);
    return null;
  }
};

/**
 * Get multi-stop route in a single API call (up to 25 waypoints).
 * Returns combined geometry for the full route.
 */
export const getMultiStopRoute = async (
  waypoints: [number, number][]
): Promise<{
  distance: number;
  duration: number;
  geometry: any;
  distanceText: string;
  durationText: string;
} | null> => {
  if (waypoints.length < 2) return null;

  // For 2 points, use the simpler endpoint
  if (waypoints.length === 2) {
    return getTruckRoute(waypoints[0], waypoints[1]);
  }

  if (!checkRateLimit()) {
    console.warn('[Mapbox] Rate limited - skipping multi-stop directions request');
    return null;
  }

  try {
    const { data, error } = await supabase.functions.invoke('directions', {
      body: { waypoints }
    });

    if (error) {
      console.error('Multi-stop directions API error:', error);
      return null;
    }

    if (data?.distance !== undefined) {
      return {
        distance: data.distance,
        duration: data.duration,
        geometry: data.geometry,
        distanceText: data.distanceText,
        durationText: data.durationText,
      };
    }

    return null;
  } catch (error) {
    console.error('Error getting multi-stop route:', error);
    return null;
  }
};

/**
 * Get a Mapbox Static Images API URL for a route with stop markers.
 * Much cheaper than interactive GL JS maps ($0.50 vs $5.00 per 1,000).
 */
export const getStaticMapUrl = async (
  geometry: { coordinates: [number, number][] },
  stops: { lng: number; lat: number; type: string; num: number }[],
  options?: { width?: number; height?: number; darkMode?: boolean }
): Promise<string | null> => {
  if (!checkRateLimit()) return null;

  try {
    const { data, error } = await supabase.functions.invoke('static-map', {
      body: {
        geometry,
        stops,
        width: options?.width ?? 800,
        height: options?.height ?? 400,
        darkMode: options?.darkMode ?? false,
      },
    });

    if (error || !data?.url) {
      console.error('Static map error:', error);
      return null;
    }

    return data.url;
  } catch (error) {
    console.error('Error getting static map:', error);
    return null;
  }
};

/**
 * Check if server has Mapbox configured by testing geocoding
 */
export const checkMapboxAvailable = async (): Promise<boolean> => {
  try {
    const token = await getMapboxToken();
    return !!token;
  } catch {
    return false;
  }
};